import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { FullScreenLoading, NUGGET_VARIANTS } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { usePageTitle } from '@backrs_ui/grow-ui/src/hooks/usePageTitle';
import { getBookmarkIdForNugget } from '@backrs_ui/grow-ui/src/pages/Bookmarks/pages/bookmarks.util';
import {
  usePostBookmarkMutation,
  useDeleteBookmarkMutation,
  useGetBookmarksQuery,
} from '@backrs_ui/grow-ui/src/redux/bookmarks/bookmarks.api';
import { useGetEmployerBySlugQuery } from '@backrs_ui/grow-ui/src/redux/employer/employer.api';
import { PagePrompt } from '@backrs_ui/grow-ui/src/redux/pagePrompts/pagePrompts';
import { useRegisterPagePrompts } from '@backrs_ui/grow-ui/src/redux/pagePrompts/useRegisterPagePrompts';

import { EmployerPage } from './EmployerPage';
import { useAgentContext } from './EmployerPageContainer.agentContext';

const EMPLOYER_PROMPTS: readonly PagePrompt[] = [
  { promptButtonId: 'employer_what_do_employees_say', title: 'What do employees say?' },
  { promptButtonId: 'employer_help_me_network', title: 'Help me network' },
  { promptButtonId: 'employer_tell_me_about_industry', title: 'Tell me about the industry' },
];

export const EmployerPageContainer = () => {
  const { employerSlug } = useParams();

  const navigate = useNavigate();
  const [postBookmark] = usePostBookmarkMutation();
  const [deleteBookmark] = useDeleteBookmarkMutation();
  const { data: bookmarks } = useGetBookmarksQuery(undefined);

  const {
    data: foundEmployer,
    isLoading,
    error,
  } = useGetEmployerBySlugQuery(employerSlug || '', {
    skip: !employerSlug,
  });

  useRegisterPagePrompts(foundEmployer ? EMPLOYER_PROMPTS : null);

  const employerBookmarkId = getBookmarkIdForNugget(
    bookmarks,
    foundEmployer?.id,
    NUGGET_VARIANTS.EMPLOYER,
  );

  useEffect(() => {
    if (!employerSlug || error) {
      navigate(APP_ROUTE.NOT_FOUND_404); // replace this with the employer search page when built!
    }
  }, [navigate, employerSlug, error]);

  usePageTitle(foundEmployer?.name);
  useAgentContext(foundEmployer);

  if (!employerSlug || isLoading || !foundEmployer || error) {
    return <FullScreenLoading />;
  }

  const handleBookmark = async () => {
    if (employerBookmarkId) {
      await deleteBookmark(employerBookmarkId);

      return;
    }

    await postBookmark({ type: 'employer', itemId: foundEmployer.id });
  };

  return (
    <EmployerPage
      employer={foundEmployer}
      handleBookmark={handleBookmark}
      hasBookmarked={!!employerBookmarkId}
    />
  );
};
